/**
 * LiveTripTracking — crew pickup / drop live tracking on a Leaflet map.
 *
 *   <LiveTripTracking transactionId={trip.transactionId} height={520} />
 *
 * airlineCode and employeeNumber are optional — creweasyMobileApi falls back to
 * session() for both. Pass them only to override.
 *
 * Self-contained: owns its own fetching (get-trip-tracking + get-vehicle-gps-location
 * only — never get-trip-driver-eta) and its own rendering, unlike TripTrackingMap.jsx/
 * LiveTripMap.jsx which split those two concerns across two files.
 *
 * Cost-conscious data flow (see the LiveTripTracking planning discussion):
 *   - get-trip-tracking is called once at mount, again whenever a GPS poll reports
 *     isReloadMap (the backend's general "something changed, re-fetch" signal — e.g.
 *     a crew member just boarded, so passengers[].isBoarded needs a refresh even
 *     though the vehicle didn't go off-route), and again when the vehicle is
 *     off-route for two consecutive GPS fixes (client-detected). Each call returns
 *     driverInfo/passengers/destination/route.mapJson/eta together, so route and ETA
 *     always refresh as one pair. isGetEta is never read — there is no separate ETA
 *     call to gate.
 *   - get-trip-driver-eta is NEVER called. ETA is recalculated on every GPS poll
 *     purely from local math: minutes = round(legMin * (remain/total)), which is
 *     structurally capped at the last eta.durationValue since remain can never
 *     exceed total (see `prog` below) — no reliance on vehicle speed for the number.
 *   - Moving/Stopped uses the GPS fix's own `speed` when present; when it's absent,
 *     it falls back to comparing the current lat/lng against the previous fix.
 *
 * Assumes payload.route.mapJson is a Google-Directions-shaped JSON (routes[0].legs[0],
 * overview_polyline.points, steps[]) — same assumption tripRoute.js's parseMapJson
 * already makes today. If the new map provider's mapJson differs, parseMapJson (or a
 * new parser) needs updating first; nothing else here depends on the provider.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './LiveTripMap.css';
import {
  routeFromPayload, measure, snapToRoute, bearing, km, minutesOf, metres,
  gpsFrom, currentPassenger, connectTrip,
} from './tripRoute';
import { getTripTracking, getVehicleGpsLocation } from '../../apis/creweasyMobileApi';
import { placeMeta } from './LiveTripMap';

const TILE = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const ACCENT = '#3ee0a3';

const vehicleIcon = () => L.divIcon({
  className: '', iconSize: [30, 30], iconAnchor: [15, 15],
  html: `<div class="ltm-veh"><i><svg viewBox="0 0 24 24" fill="#07150f">
    <path d="M12 2 21 21l-9-5-9 5z"/></svg></i></div>`,
});
const targetIcon = (meta) => L.divIcon({
  className: '', iconSize: [34, 42], iconAnchor: [17, 40],
  html: `<div class="ltm-pin"><svg viewBox="0 0 34 42">
    <path d="M17 2C9.8 2 4 7.8 4 15c0 9.8 13 25 13 25s13-15.2 13-25c0-7.2-5.8-13-13-13z"
      fill="${ACCENT}" stroke="#eafff6" stroke-width="2"/>
    <g transform="translate(9 6.6) scale(.66)" fill="#07150f"><path d="${meta.glyph}"/></g></svg></div>`,
});
const geofenceLabel = (meta) => L.divIcon({
  className: '', iconSize: [0, 0], iconAnchor: [-15, 30],
  html: `<div class="ltm-geolabel"><svg viewBox="0 0 24 24"><path d="${meta.glyph}"/></svg>
    <b>${meta.kind}</b><em>${meta.outer} m</em></div>`,
});
const crewIcon = (type) => L.divIcon({
  className: '', iconSize: [28, 28], iconAnchor: [14, 14],
  html: `<div class="ltm-crewpin"><i>${type || ''}</i></div>`,
});
const dropIcon = () => L.divIcon({
  className: '', iconSize: [28, 34], iconAnchor: [14, 32],
  html: `<div class="ltm-drop"><svg viewBox="0 0 28 34">
    <path d="M14 2C8.5 2 4 6.5 4 12c0 7.4 10 20 10 20s10-12.6 10-20c0-5.5-4.5-10-10-10z"
      fill="#1b232c" stroke="#8e9aa6" stroke-width="2"/>
    <circle cx="14" cy="12" r="3.6" fill="#8e9aa6"/></svg></div>`,
});

// half dial: middle-left → top → middle-right, r = 68 around (86, 74)
const OFF_ROUTE_M = 150;   // beyond this the plotted route is treated as stale
const MOVING_M = 10;       // beyond this since the last fix, count as "Moving" when no speed field
const ARC_LEN = Math.PI * 68;

// Whole-route congestion, from route.durationValue vs route.trafficValue (mapthrust/Directions
// gives no per-segment traffic data — traffic_speed_entry is always empty — so this colors the
// entire drawn route by how much duration_in_traffic exceeds the traffic-free duration.
const TRAFFIC_COLORS = { light: ACCENT, moderate: '#ffb35c', heavy: '#ff5c5c' };
const trafficLevel = (ratio) => (ratio >= 1.8 ? 'heavy' : ratio >= 1.3 ? 'moderate' : 'light');
const dialPoint = (f) => {
  const a = Math.PI * (1 - Math.min(1, Math.max(0, f)));
  return { x: 86 + 68 * Math.cos(a), y: 74 - 68 * Math.sin(a) };
};

export default function LiveTripTracking({
  airlineCode, transactionId, employeeNumber, height = 520, gpsIntervalMs = 10000, className,
  onDeparted,
}) {
  const mapEl = useRef(null);
  const map = useRef(null);
  const layers = useRef({ route: [], vehicle: null, trail: null, core: null, drop: null });
  const followRef = useRef(true);
  const spokenStep = useRef(-1);
  const prevFix = useRef(null);
  const departedRef = useRef(false); // fires onDeparted only once

  const [tracking, setTracking] = useState(null);
  // Mirrors CrewActivityMobileViewComponent.jsx's loadTripTracking: true for the duration of
  // each get-trip-tracking call (initial load and any later replot()), false once it settles —
  // an explicit flag tied to the request itself, not inferred from tracking/gps data shape.
  const [trackingLoading, setTrackingLoading] = useState(true);
  const [gps, setGps] = useState(null);
  const [moving, setMoving] = useState(null); // true/false once derived from position delta; null = unknown
  const [hasLiveFix, setHasLiveFix] = useState(false); // true once a real get-vehicle-gps-location poll has landed
  const [gpsFailed, setGpsFailed] = useState(false); // a get-vehicle-gps-location poll errored, or resolved with no payload (e.g. success:false)
  const [arriving, setArriving] = useState(false); // client-computed: within 50m of destination, not yet isDeparted
  const [live, setLive] = useState({ remain: null, offRoute: 0 });
  const conn = useRef(null);
  const strikes = useRef(0);
  const heading = useRef(0);
  const [follow, setFollow] = useState(true);
  const [tab, setTab] = useState('crew');
  const [open, setOpen] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);   // voice guidance on by default
  const [activeStep, setActiveStep] = useState(0);

  const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
  const ready = Boolean(transactionId);

  useEffect(() => { followRef.current = follow; }, [follow]);
  // Backend-confirmed drop — fire onDeparted (once) after a short delay so the "Crew Dropped"
  // confirmation in the gauge is visible before the caller closes this panel.
  useEffect(() => {
    if (!gps?.isDeparted || departedRef.current) return undefined;
    departedRef.current = true;
    const t = setTimeout(() => onDeparted?.(), 2500);
    return () => clearTimeout(t);
  }, [gps?.isDeparted, onDeparted]);
  useEffect(() => () => synth?.cancel(), [synth]);

  /* get-trip-tracking once, then get-vehicle-gps-location on an interval. get-trip-tracking
     is re-called only via conn.replot() (off-route strikes, below) — never on a timer, and
     get-trip-driver-eta is never called at all. */
  useEffect(() => {
    if (!ready) return undefined;
    const params = { airlineCode, employeeNumber, transactionId };
    const c = connectTrip({
      getTracking: () => {
        setTrackingLoading(true);
        return getTripTracking(params)
          .then((res) => {
            // connectTrip only calls onTracking (where we normally clear trackingLoading,
            // paired with setTracking so the loader and the data it's gating appear/disappear
            // in the very same render — not a run() then a later, separately-committed
            // setTrackingLoading(false) that briefly shows an empty map in between) when the
            // response actually has a payload. An empty response never reaches onTracking, so
            // clear it here instead or the spinner would be stuck on forever.
            const p = res && res.payload !== undefined ? res.payload : res;
            if (!p) setTrackingLoading(false);
            return res;
          })
          .catch((err) => { setTrackingLoading(false); throw err; });
      },
      // getVehicleGpsLocation already unwraps to just the payload (creweasyMobileApi.js's
      // payloadOf), returning null when the response has no payload (e.g. success:false) —
      // treat that, and any thrown/network error, as a failed poll rather than silently
      // doing nothing, so the UI can tell "still waiting on the first response" apart from
      // "the API isn't returning a usable position."
      getGps: () => getVehicleGpsLocation(params)
        .then((res) => { if (!res) setGpsFailed(true); return res; })
        .catch((err) => { setGpsFailed(true); throw err; }),
      gpsIntervalMs,
      onTracking: (p) => {
        setTracking(p);
        setTrackingLoading(false);
        const g = gpsFrom(p);
        if (g) setGps((prev) => ({ ...prev, ...g }));
        setLive({ remain: null, offRoute: 0 });
        strikes.current = 0;
      },
      onGps: (p) => {
        const fix = {
          latitude: +p.latitude, longitude: +p.longitude,
          speed: p.speed != null ? +p.speed : null,
          bearing: p.bearing != null ? +p.bearing : null,
          altitude: p.altitude != null ? +p.altitude : null,
          gpsTime: p.gpsTime || '', address: '',
          isDeparted: !!p.isDeparted,
        };
        setGps(fix);
        setHasLiveFix(true);
        setGpsFailed(false);
        // No real `speed` reading — infer movement from how far this fix is from the
        // last one (see file header).
        if (fix.speed == null) {
          const prev = prevFix.current;
          setMoving(prev ? metres([prev.latitude, prev.longitude], [fix.latitude, fix.longitude]) > MOVING_M : null);
        } else {
          setMoving(fix.speed > 0);
        }
        prevFix.current = { latitude: fix.latitude, longitude: fix.longitude };
        // Backend's general "something changed" signal (e.g. a crew member just
        // boarded) — re-fetch get-trip-tracking so passengers/route/eta refresh
        // together, independent of the off-route strikes check below. isGetEta is
        // deliberately never read here — there's no separate ETA call to gate.
        if (p.isReloadMap) c.replot();
      },
      onError: (err) => console.warn('[LiveTripTracking] trip poll failed', err),
    });
    conn.current = c;
    return () => { c.stop(); conn.current = null; };
  }, [ready, airlineCode, transactionId, employeeNumber, gpsIntervalMs]);

  const meCrew = currentPassenger(tracking?.passengers);
  const boarded = !!(tracking?.eta?.isBoarded || meCrew?.isBoarded);
  const route = useMemo(() => routeFromPayload(tracking), [tracking]);
  const geom = useMemo(() => (route ? measure(route.points) : null), [route]);
  const traffic = useMemo(() => {
    if (!route?.durationValue || !route?.trafficValue) return null;
    const ratio = route.trafficValue / route.durationValue;
    return { level: trafficLevel(ratio), ratio, trafficText: route.trafficText, durationText: route.durationText };
  }, [route]);
  /* The dial measures the CURRENT leg: vehicle → pickup, or vehicle → destination once
     boarded. eta.distanceValue/durationValue anchor it from the last get-trip-tracking
     call; live.remain keeps it current from each GPS fix. Because remain can never exceed
     total (both derived from the same non-negative cumulative-distance array), minutes can
     never exceed legMin — the Math.min below is a cheap explicit belt-and-suspenders on
     top of that structural guarantee. */
  const prog = useMemo(() => {
    const e = tracking?.eta || {};
    const total = e.distanceValue || geom?.total || 0;
    const remain = Math.min(total, live.remain != null ? live.remain
      : (e.distanceValue != null ? e.distanceValue : total));
    const legMin = e.durationValue || minutesOf(e.durationText);
    const minutes = Math.min(legMin || 0, Math.max(0, Math.round(legMin * (remain / (total || 1)))));
    // total === 0 isn't "arriving in under a minute" — it's a degenerate/zero-length leg (e.g.
    // the vehicle's last-known position is already at the pickup point), and showing `<1 mins`
    // for that reads as a live countdown when there's really no distance to base one on.
    let message;
    if (!hasLiveFix) {
      // Prefer the server's own message from the initial get-trip-tracking call (already
      // available before any GPS poll lands) over a generic placeholder.
      message = e.trackingMessage || 'Waiting for live location…';
    } else if (total === 0) {
      message = boarded ? 'At the drop point' : 'Driver on site';
    } else if (live.remain == null && e.trackingMessage) {
      message = e.trackingMessage;
    } else {
      message = `${boarded ? 'Drop' : 'Pickup'} in ${minutes < 1 ? '<1' : minutes} min${minutes === 1 ? '' : 's'}`;
    }
    return {
      total, remain, covered: Math.max(0, total - remain),
      pct: total ? Math.min(100, Math.max(0, Math.round((1 - remain / total) * 100))) : 0,
      minutes,
      message,
    };
  }, [tracking, geom, live.remain, boarded, hasLiveFix]);
  const geo = gps;

  /* map init */
  useEffect(() => {
    if (map.current || !mapEl.current) return undefined;
    const m = L.map(mapEl.current, { zoomControl: true, attributionControl: true })
      .setView([19.076, 72.877], 12);
    L.tileLayer(TILE, { maxZoom: 19, attribution: '© OpenStreetMap contributors' }).addTo(m);
    m.zoomControl.setPosition('bottomright');
    map.current = m;
    const ro = new ResizeObserver(() => m.invalidateSize());
    ro.observe(mapEl.current);
    return () => { ro.disconnect(); m.remove(); map.current = null; };
  }, []);

  /* route, geofence, crew and drop markers */
  useEffect(() => {
    const m = map.current;
    if (!m || !route || !tracking) return;

    layers.current.route.forEach((l) => m.removeLayer(l));
    layers.current.route = [];
    const add = (l) => { l.addTo(m); layers.current.route.push(l); return l; };

    route.alternates.forEach((a) => add(
      L.polyline(a.points, { color: '#6f7d8c', weight: 3.5, opacity: 0.6, lineCap: 'round' })
        .bindTooltip(`Alternative · ${a.summary} · ${a.distanceText} · ${a.trafficText}`, { sticky: true }),
    ));
    const routeColor = TRAFFIC_COLORS[traffic?.level || 'light'];
    add(L.polyline(route.points, { color: routeColor, weight: 15, opacity: 0.13, lineCap: 'round', interactive: false }));
    layers.current.core = add(L.polyline(route.points, { color: routeColor, weight: 5, opacity: 0.4, lineCap: 'round' }));
    add(L.polyline(route.points, {
      color: '#eafff6', weight: 2.4, opacity: 0.9, className: 'ltm-flow', interactive: false,
    }));
    layers.current.trail = add(L.polyline([], { color: ACCENT, weight: 6, lineCap: 'round', interactive: false }));

    const dest = tracking.destination;
    const nextCrew = currentPassenger(tracking.passengers);
    const targetLL = boarded ? [dest.latitude, dest.longitude] : route.end;
    const gf = boarded
      ? placeMeta(dest, dest.address)
      : placeMeta(null, nextCrew?.pickupLocation || route.endAddress);

    add(L.circle(targetLL, { radius: gf.outer, className: 'ltm-halo', color: ACCENT, weight: 1, fillColor: ACCENT, fillOpacity: 0.1, interactive: false }));
    add(L.circle(targetLL, { radius: gf.inner, className: 'ltm-halo2', color: '#8bf0cd', weight: 1, fillColor: ACCENT, fillOpacity: 0.2, interactive: false }));
    // Kept on `layers.current.drop` (not just pushed into the generic route-layer list) so the
    // vehicle-position effect below can toggle a pulse class on it every GPS poll without
    // having to redraw the whole route/marker set.
    layers.current.drop = add(L.circle(targetLL, { radius: gf.outer, color: ACCENT, weight: 1.4, opacity: 0.7, dashArray: '4 8', fill: false, interactive: false }));
    add(L.marker(targetLL, { zIndexOffset: 900, icon: targetIcon(gf) }).bindTooltip(
      boarded ? `Drop · ${String(dest.address).slice(0, 90)}` : `Pickup · ${nextCrew?.pickupLocation || route.endAddress}`,
      { direction: 'top', offset: [0, -42] },
    ));
    add(L.marker(targetLL, { interactive: false, zIndexOffset: 890, icon: geofenceLabel(gf) }));

    if (!boarded) {
      (tracking.passengers || []).filter((p) => !p.isBoarded).forEach((p) => add(
        L.marker([p.pickupLatitude, p.pickupLongitude], { zIndexOffset: 880, icon: crewIcon(p.airlineCrewType) })
          .bindTooltip(`${p.crewName} · pickup ${p.pickupTime} · ${p.tripStatus}`, { direction: 'top', offset: [0, -15] }),
      ));
      add(L.marker([dest.latitude, dest.longitude], { icon: dropIcon() })
        .bindTooltip('Final drop', { direction: 'top', offset: [0, -34] }));
    }

    add(L.marker(route.start, {
      icon: L.divIcon({ className: '', iconSize: [20, 20], iconAnchor: [10, 10], html: '<div class="ltm-pin-o"></div>' }),
    }).bindTooltip(`Route start · ${route.startAddress}`, { direction: 'top', offset: [0, -12] }));
  }, [route, tracking, boarded, traffic]);

  /* vehicle position, trail, active step, voice */
  useEffect(() => {
    const m = map.current;
    if (!m || !route || !geom || !geo?.latitude) return;
    const lat = +geo.latitude, lng = +geo.longitude;

    if (!layers.current.vehicle) {
      layers.current.vehicle = L.marker([lat, lng], { zIndexOffset: 1000, icon: vehicleIcon() }).addTo(m);
    } else {
      layers.current.vehicle.setLatLng([lat, lng]);
    }
    const dv = tracking?.driverInfo;
    if (dv) {
      layers.current.vehicle.unbindTooltip().bindTooltip(
        `${dv.vehicleNo} · ${dv.driverName}<br>${geo.address || ''}`,
        { direction: 'top', offset: [0, -17] },
      );
    }

    const { index, offRouteMetres } = snapToRoute(route.points, lat, lng);

    /* Prefer the GPS bearing; at a standstill it is meaningless, so hold the last
       heading, and fall back to the route direction when the field is absent. */
    const isMoving = gps?.speed != null ? gps.speed > 1 : !!moving;
    const reported = gps?.bearing != null && (isMoving || gps.bearing > 0) ? gps.bearing : null;
    const nxt = route.points[Math.min(index + 1, route.points.length - 1)];
    heading.current = reported != null ? reported
      : (isMoving ? bearing([lat, lng], nxt) : heading.current);
    const arrow = layers.current.vehicle.getElement()?.querySelector('svg');
    if (arrow) arrow.style.transform = `rotate(${heading.current}deg)`;
    if (layers.current.trail) {
      layers.current.trail.setLatLngs(route.points.slice(0, index + 1).concat([[lat, lng]]));
    }

    // 50m-radius "arriving at drop" highlight — client-computed and provisional, distinct from
    // the backend-confirmed gps.isDeparted below. Only meaningful once boarded (heading to the
    // final destination) and before the backend has confirmed the drop.
    const dest = tracking?.destination;
    const isArriving = boarded && !gps?.isDeparted && !!dest
      && metres([lat, lng], [dest.latitude, dest.longitude]) <= 50;
    setArriving(isArriving);
    const dropEl = layers.current.drop && layers.current.drop.getElement();
    if (dropEl) dropEl.classList.toggle('ltm-pulsing', isArriving);

    setLive({ remain: Math.max(0, geom.total - geom.cum[index]), offRoute: offRouteMetres });

    /* off route for two fixes running → the plotted route (and its paired eta) is
       stale, refetch get-trip-tracking. This is the second redraw trigger, alongside
       isReloadMap handled in onGps above — this one is purely client-detected. */
    if (offRouteMetres > OFF_ROUTE_M) {
      strikes.current += 1;
      if (strikes.current >= 2) { strikes.current = 0; if (conn.current) conn.current.replot(); }
    } else strikes.current = 0;

    let si = 0;
    route.steps.forEach((s, n) => {
      if (geom.cum[index] >= geom.cum[snapToRoute(route.points, s.at[0], s.at[1]).index]) si = n;
    });
    if (voiceOn && synth && si !== spokenStep.current) {
      const cur = route.steps[si], after = route.steps[si + 1];
      if (cur && (spokenStep.current !== -1 || si > 0)) {
        const u = new SpeechSynthesisUtterance(
          after ? `${cur.text}. Then in ${cur.distanceText}, ${after.text.toLowerCase()}.`
            : `${cur.text}. Arriving now.`,
        );
        u.lang = 'en-IN';
        synth.speak(u);
      }
      spokenStep.current = si;
    }
    setActiveStep(si);

    if (followRef.current) m.panTo([lat, lng], { animate: true, duration: 0.6 });
  }, [geo?.latitude, geo?.longitude, gps, moving, route, geom, tracking, voiceOn, synth]);

  if (!ready) return null;

  const fitRoute = () => {
    const m = map.current;
    if (!m || !route) return;
    setFollow(false);
    const b = L.latLngBounds(route.points);
    if (geo?.latitude) b.extend([+geo.latitude, +geo.longitude]);
    m.fitBounds(b, { paddingTopLeft: [290, 40], paddingBottomRight: [235, 50] });
  };
  const flyTo = (ll) => { setFollow(false); if (map.current) map.current.flyTo(ll, 16, { duration: 0.7 }); };

  const dv = tracking?.driverInfo;
  const crew = tracking?.passengers || [];
  const dest = tracking?.destination;
  const boardedCount = crew.filter((p) => p.isBoarded).length;
  const nextCrew = meCrew;
  const destKind = dest
    ? (dest.isResidence ? 'Residence' : dest.isAirport ? 'Airport' : dest.isHotel ? 'Hotel' : 'Ground duty')
    : '';
  const arrival = prog.minutes
    ? new Date(Date.now() + prog.minutes * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '—';
  const knob = dialPoint(prog.pct / 100);

  const summaryText = (() => {
    if (tab === 'crew') {
      return nextCrew
        ? `${nextCrew.isCurrentPassenger ? 'You' : 'Next'}: ${nextCrew.crewName} · ${nextCrew.pickupTime} · ${nextCrew.tripStatus}`
        : `All ${crew.length} crew boarded`;
    }
    if (tab === 'dirs') {
      const s = route?.steps?.[activeStep] || route?.steps?.[0];
      return s ? `${activeStep + 1}. ${s.text} · ${s.distanceText}` : 'No turn list on this route';
    }
    return dest ? `${destKind} · ${String(dest.address).slice(0, 70)}…` : '—';
  })();

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    spokenStep.current = -1;
    if (!next) { if (synth) synth.cancel(); return; }
    const u = new SpeechSynthesisUtterance(
      `Voice guidance on. ${prog.message}${nextCrew ? ` for ${nextCrew.crewName.replace(/\s*\[.*\]/, '')}` : ''}.`,
    );
    u.lang = 'en-IN';
    synth.cancel();
    synth.speak(u);
  };

  return (
    <div className={`ltm ${className || ''}`} style={{ height, width: '100%' }}>
      <div ref={mapEl} className="ltm-map" />
      {trackingLoading && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 900, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--ltm-muted)',
          background: 'var(--ltm-bg)',
        }}>
          <i className="fas fa-sync-alt" style={{ fontSize: 22, color: 'var(--ltm-accent)', animation: 'spin 0.8s linear infinite' }} />
          <span style={{ fontSize: 12.5, fontWeight: 700 }}>Waiting for live location…</span>
        </div>
      )}

      <div className="ltm-leftstack">
        {dv && (
          <div className="ltm-driver ltm-glass">
            {dv.transporterName && (
              <>
              <span className="ltm-block-label">Transporter</span>
              <div className="ltm-transporter">
                <span className="ltm-transporter-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M4 3h9a1 1 0 0 1 1 1v6h5a1 1 0 0 1 1 1v9h-8v-4H9v4H4V3z" />
                    <rect x="6.5" y="5.5" width="2" height="2" />
                    <rect x="10.5" y="5.5" width="2" height="2" />
                    <rect x="6.5" y="9.5" width="2" height="2" />
                    <rect x="10.5" y="9.5" width="2" height="2" />
                    <rect x="15.5" y="11.5" width="2" height="2" />
                  </svg>
                </span>
                <div className="ltm-transporter-col">
                  <span className="ltm-transporter-name">{dv.transporterName}</span>
                </div>
              </div>
              </>
            )}
            <span className="ltm-block-label">Driver</span>
            <div className="ltm-driver-body">
              <div className="ltm-avatar">
                {dv.driverPhotoUrl ? (
                  <img src={String(dv.driverPhotoUrl).replace(/^http:\/\//, 'https://')} alt={dv.driverName}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : null}
                <span>{dv.driverName.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()}</span>
              </div>
              <div className="ltm-dinfo">
                <div className="ltm-drow">
                  <div className="ltm-dname">{dv.driverName}</div>
                </div>
                <div className="ltm-dcall">
                  <span className="ltm-dphone ltm-mono">{dv.driverMobileNo}</span>
                  <a className="ltm-call" href={`tel:${String(dv.driverMobileNo).replace(/[^\d+]/g, '')}`}
                    title={`Call ${dv.driverMobileNo}`} aria-label="Call driver">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8c1.1 2.2 2.9 4 5.1 5.1l1.7-1.7c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1V19c0 .6-.4 1-1 1-8.3 0-15-6.7-15-15 0-.6.4-1 1-1h3.4c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.3 0 .7-.2 1l-2.1 1.3z" /></svg>
                  </a>
                </div>
                <div className="ltm-dmeta">
                  <span className="ltm-plate ltm-mono">{dv.vehicleNo}</span>
                  <span className="ltm-vtype">{dv.vehicleType}</span>
                </div>
              </div>
            </div>
            {meCrew?.crewName && (
              <>
              <span className="ltm-block-label">Crew (tracked)</span>
              <div className="ltm-driver-body" title="Crew being tracked on this map">
                <div className="ltm-avatar">
                  {meCrew.crewProfilePhotoUrl ? (
                    <img
                      src={String(meCrew.crewProfilePhotoUrl).replace(/^http:\/\//, 'https://')}
                      alt={meCrew.crewName}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : null}
                  <span>{meCrew.crewName.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()}</span>
                </div>
                <div className="ltm-tracked-name">{meCrew.crewName}</div>
              </div>
              </>
            )}
          </div>
        )}

        <div className="ltm-controls ltm-glass">
          <div className="ltm-segs">
            {[['crew', `Crew`], ['dirs', 'Route'], ['drop', boarded ? 'Drop' : 'Pickup']].map(([k, label]) => (
              <button key={k} className="ltm-tab" aria-selected={tab === k}
                onClick={() => { setTab(k); setOpen(true); }}>
                {label}{k === 'crew' && <span className="ltm-count">{boardedCount}/{crew.length}</span>}
              </button>
            ))}
          </div>
          <div className="ltm-acts">
            <button className="ltm-mini" aria-pressed={follow} onClick={() => setFollow((f) => !f)}>Follow</button>
            <button className="ltm-mini" onClick={fitRoute}>Fit route</button>
            <button className="ltm-mini ltm-ic" aria-pressed={voiceOn} disabled={!synth}
              title={synth ? 'Spoken trip updates' : 'No speech synthesis in this browser'}
              onClick={toggleVoice}>
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M4 9v6h3.5L13 20V4L7.5 9H4z" />
                <path className="ltm-wave" d="M16 8.5a4.5 4.5 0 0 1 0 7v-2a2.5 2.5 0 0 0 0-3v-2zm2.6-3a8 8 0 0 1 0 13v-2a6 6 0 0 0 0-9v-2z" />
              </svg>
              <span className="ltm-sr">{voiceOn ? 'Voice on' : 'Voice off'}</span>
            </button>
          </div>
        </div>

        <section className="ltm-details ltm-glass" data-open={open}>
          <button className="ltm-dhead" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            <span className="ltm-ttl">
              {tab === 'crew' ? `Crew ${boardedCount}/${crew.length}` : tab === 'dirs' ? 'Route' : (boarded ? 'Drop' : 'Pickup')}
            </span>
            <svg className="ltm-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <div className="ltm-dsum">{summaryText}</div>
          <div className="ltm-dbody">
            {tab === 'crew' && (
              <div className="ltm-pane">
                {crew.map((p) => (
                  <button key={p.employeeNumber}
                    className={`ltm-crew ${p.isBoarded ? 'boarded' : ''} ${p.isCurrentPassenger ? 'me' : ''}`}
                    onClick={() => flyTo([p.pickupLatitude, p.pickupLongitude])}>
                    <span className="ltm-badge">{p.airlineCrewType}</span>
                    <span className="ltm-ctop">
                      <span className="ltm-cname">{p.crewName}</span>
                      {p.isCurrentPassenger && <span className="ltm-you">You</span>}
                      <span className="ltm-status">{p.tripStatus}</span>
                    </span>
                    <span className="ltm-cloc">
                      {p.isBoarded ? `Boarded ${p.boardedTime}`
                        : <>{p.pickupLocation} <em>· {p.pickupTime}</em></>}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {tab === 'dirs' && (
              <div className="ltm-pane">
                {(route?.steps || []).map((s, n) => (
                  <button key={n} className={`ltm-step ${n === activeStep ? 'active' : ''} ${n < activeStep ? 'done' : ''}`}
                    onClick={() => flyTo(s.at)}>
                    <span className="ltm-n ltm-mono">{n + 1}</span>
                    <span className="ltm-t">{s.text}</span>
                    <span className="ltm-d ltm-mono">{s.distanceText}</span>
                  </button>
                ))}
              </div>
            )}

            {tab === 'drop' && dest && (
              <div className="ltm-pane ltm-dropinfo">
                {!boarded && meCrew?.pickupLocation && (
                  <>
                    <b>Pickup</b>{meCrew.pickupLocation}
                    <span className="ltm-tag">{meCrew.pickupTime}</span>
                  </>
                )}
                <b>Final drop</b>{dest.address}
                <span className="ltm-tag">{destKind}</span>
                <span className="ltm-tag ltm-mono">{dest.latitude}, {dest.longitude}</span>
                <span className="ltm-tag">
                  Routing to {boarded ? 'drop' : 'crew pickup'} · via {route?.summary} · {route?.distanceText}
                </span>
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="ltm-card ltm-gauge">
        <div className="ltm-ghead">
          <span className="ltm-lbl">{boarded ? 'Drop in' : 'Pickup in'}</span>
          <span className="ltm-glive"><i />Live</span>
        </div>
        <div className="ltm-gmsg" style={gps?.isDeparted ? { color: 'var(--ltm-accent)' } : undefined}>
          {gps?.isDeparted ? 'Crew Dropped ✓' : prog.message}
        </div>
        {arriving && (
          <div className="ltm-gwarn" style={{
            background: 'rgba(62,224,163,.14)', borderColor: 'rgba(62,224,163,.4)', color: 'var(--ltm-accent)',
          }}>
            Arriving at drop point
          </div>
        )}
        {gps?.speed != null ? (
          <div className="ltm-speed-row">
            <div className="ltm-speed-badge">
              <b className="ltm-speed-value">{Math.round(gps.speed)}</b><span>km/h</span>
            </div>
            <div className={`ltm-status-pill ${gps.speed > 0 ? 'is-moving' : 'is-stopped'}`}>
              {gps.speed > 0 ? 'Moving' : 'Stopped'}
            </div>
          </div>
        ) : moving != null ? (
          <div className="ltm-speed-row">
            <div className={`ltm-status-pill ${moving ? 'is-moving' : 'is-stopped'}`}>
              {moving ? 'Moving' : 'Stopped'}
            </div>
          </div>
        ) : gpsFailed ? (
          <div className="ltm-gwarn">
            No GPS position reported — position may be stale.
          </div>
        ) : (
          <div className="ltm-gwarn">
            Locating GPS position — position may be stale.
          </div>
        )}
        {traffic && traffic.level !== 'light' && (
          <div className="ltm-gwarn" style={{
            background: traffic.level === 'heavy' ? 'rgba(255,92,92,.14)' : 'rgba(255,179,92,.12)',
            borderColor: traffic.level === 'heavy' ? 'rgba(255,92,92,.4)' : 'rgba(255,179,92,.38)',
            color: TRAFFIC_COLORS[traffic.level],
          }}>
            {traffic.level === 'heavy' ? 'Heavy traffic' : 'Moderate traffic'} on this route
            {' · '}{traffic.trafficText}{traffic.durationText ? ` (usual ${traffic.durationText})` : ''}
          </div>
        )}
        {(live.offRoute > OFF_ROUTE_M || (hasLiveFix && gps && !gps.gpsTime)) && (
          <div className="ltm-gwarn">
            {live.offRoute > OFF_ROUTE_M
              ? `Vehicle ${live.offRoute} m off the plotted route — refreshing the route…`
              : 'No GPS timestamp reported — position may be stale.'}
          </div>
        )}

        <div className="ltm-dial">
          <svg viewBox="0 0 172 92" aria-hidden="true">
            <path d="M18 74 A 68 68 0 0 1 154 74" fill="none" stroke="rgba(255,255,255,.10)"
              strokeWidth="9" strokeLinecap="round" />
            <path d="M18 74 A 68 68 0 0 1 154 74" fill="none" stroke={ACCENT} strokeWidth="9"
              strokeLinecap="round" strokeDasharray={ARC_LEN}
              strokeDashoffset={ARC_LEN * (1 - prog.pct / 100)}
              style={{ transition: 'stroke-dashoffset .45s ease' }} />
            <g stroke="rgba(255,255,255,.18)" strokeWidth="2.5" strokeLinecap="round">
              <line x1="30" y1="74" x2="52" y2="74" />
              <line x1="120" y1="74" x2="142" y2="74" />
              <line x1="86" y1="6" x2="86" y2="20" />
            </g>
            <circle cx="86" cy="74" r="7" fill="#141a21" stroke="rgba(255,255,255,.22)" strokeWidth="2" />
            <circle cx={knob.x} cy={knob.y} r="4.5" fill="#eafff6"
              style={{ transition: 'cx .45s ease, cy .45s ease' }} />
          </svg>
          <div className="ltm-dialtext">
            <b className="ltm-mono">{km(prog.remain)}</b>
            <span>remaining</span>
          </div>
        </div>

        <div className="ltm-gstats">
          <div><div className="ltm-gk">Covered</div><div className="ltm-gv ltm-mono">{km(prog.covered)}</div></div>
          <div className="ltm-rule" />
          <div><div className="ltm-gk">Progress</div><div className="ltm-gv ltm-mono">{prog.pct}%</div></div>
          <div className="ltm-rule ltm-rule2" />
          <div className="ltm-third"><div className="ltm-gk">Arrival</div><div className="ltm-gv ltm-mono">{arrival}</div></div>
        </div>

        <div className="ltm-gfoot">
          {tracking?.eta?.routeDistanceText || km(geom?.total || 0)} whole trip
          {' · via '}{route?.summary}
          {' · GPS '}{gps?.gpsTime || 'time not reported'}
          {gps?.altitude ? ` · ${Math.round(gps.altitude)} m alt` : ''}
        </div>
      </div>
    </div>
  );
}
